import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Fingerprint, RefreshCw, Users, ClipboardList, CreditCard,
         ScanFace, CheckCircle, XCircle, Download, Plus, Pencil,
         Trash2, Ban, Play, X, KeyRound, Upload, Camera, CalendarClock,
         Send } from 'lucide-react';
import api from '../services/api';

const fmt = (t) => (t ? new Date(t).toLocaleString('es-MX') : '—');

// Modo de verificacion tal como lo reporta el terminal.
const MODO = {
  faceOrFpOrCardOrPw: 'Rostro/Huella/Tarjeta/Código',
  face: 'Rostro',
  fp: 'Huella',
  card: 'Tarjeta',
  invalid: '—',
};
const modoOf = (m) => MODO[m] || m || '—';

// Clasificacion que calcula el backend contra el horario de cada persona.
const ESTADO = {
  a_tiempo:    { txt: 'A tiempo',      c: '#065f46', bg: '#d1fae5' },
  retardo:     { txt: 'Retardo',       c: '#92400e', bg: '#fef3c7' },
  fuera_turno: { txt: 'Fuera de turno', c: '#991b1b', bg: '#fee2e2' },
  sin_horario: { txt: 'Sin horario',   c: '#6b7280', bg: '#f3f4f6' },
};

const DIAS = [
  { n: 1, lbl: 'Lunes' }, { n: 2, lbl: 'Martes' }, { n: 3, lbl: 'Miércoles' },
  { n: 4, lbl: 'Jueves' }, { n: 5, lbl: 'Viernes' }, { n: 6, lbl: 'Sábado' },
  { n: 0, lbl: 'Domingo' },
];

const DIA_VACIO = {
  habilitado: false, entrada_desde: '08:00', entrada_hasta: '09:10',
  entrada_oficial: '09:00', salida_desde: '17:00', salida_hasta: '19:00',
};

const hhmm = (v) => (v ? String(v).slice(0, 5) : '');

const VACIO = {
  employee_no: '', nombre: '', password: '',
  valid_desde: '2026-01-01T00:00:00', valid_hasta: '2037-12-31T23:59:59',
  activo: true,
};

const Accesos = () => {
  const [tab, setTab] = useState('checadas');
  const [devices, setDevices] = useState([]);
  const [deviceId, setDeviceId] = useState('');
  const [events, setEvents] = useState([]);
  const [users, setUsers] = useState([]);
  const [usersVia, setUsersVia] = useState('');
  const [usersErr, setUsersErr] = useState(null);
  const [usersLoading, setUsersLoading] = useState(false);
  const [summary, setSummary] = useState({});
  const [soloMarcajes, setSoloMarcajes] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [msg, setMsg] = useState(null);

  // Modales
  const [form, setForm] = useState(null);            // null = cerrado
  const [editando, setEditando] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [confirmar, setConfirmar] = useState(null);  // {tipo, user}
  const [captura, setCaptura] = useState(null);      // texto de progreso
  const fileRef = useRef(null);

  // Horarios
  const [horarios, setHorarios] = useState([]);
  const [formH, setFormH] = useState(null);
  const [guardandoH, setGuardandoH] = useState(false);

  const loadDevices = useCallback(async () => {
    const r = await api.get('/api/access/devices');
    setDevices(r.data || []);
    if (r.data && r.data.length) setDeviceId((cur) => cur || r.data[0].device_id);
  }, []);

  const loadEvents = useCallback(async () => {
    if (!deviceId) return;
    const [e, s] = await Promise.all([
      api.get('/api/access/events?device_id=' + deviceId
              + '&solo_marcajes=' + soloMarcajes + '&limit=300'),
      api.get('/api/access/summary?device_id=' + deviceId),
    ]);
    setEvents(e.data || []);
    setSummary(s.data || {});
  }, [deviceId, soloMarcajes]);

  // El personal se lee DEL TERMINAL: es la fuente de verdad de que biometria
  // tiene enrolada cada persona.
  const loadUsers = useCallback(async () => {
    if (!deviceId) return;
    setUsersLoading(true);
    setUsersErr(null);
    try {
      const r = await api.get('/api/access/' + deviceId + '/users');
      setUsers((r.data && r.data.users) || []);
      setUsersVia((r.data && r.data.via) || '');
      if (r.data && r.data.error) setUsersErr(r.data.error);
    } catch (e) {
      setUsers([]);
      setUsersErr((e.response && e.response.data && e.response.data.detail) || e.message);
    } finally {
      setUsersLoading(false);
    }
  }, [deviceId]);

  const loadHorarios = useCallback(async () => {
    const r = await api.get('/api/access/horarios');
    setHorarios(r.data || []);
  }, []);

  useEffect(() => { loadDevices(); }, [loadDevices]);
  useEffect(() => { if (tab === 'horarios') loadHorarios(); }, [tab, loadHorarios]);
  useEffect(() => { if (deviceId) loadEvents(); }, [deviceId, soloMarcajes, loadEvents]);
  useEffect(() => { if (deviceId && tab === 'personal') loadUsers(); }, [deviceId, tab, loadUsers]);

  // Refresco automatico solo en checadas: el de personal golpea al equipo.
  useEffect(() => {
    if (tab !== 'checadas') return undefined;
    const t = setInterval(loadEvents, 20000);
    return () => clearInterval(t);
  }, [tab, loadEvents]);

  const sync = async () => {
    setSyncing(true);
    setMsg(null);
    try {
      const r = await api.post('/api/access/sync?device_id=' + deviceId);
      const d = r.data || {};
      setMsg(d.error ? { t: 'err', x: d.error }
        : { t: 'ok', x: d.leidos + ' leídos, ' + d.nuevos + ' nuevos (vía ' + d.via + ')' });
      await loadEvents();
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setSyncing(false); }
  };

  // ---- alta / edicion -------------------------------------------------
  const abrirAlta = () => { setForm({ ...VACIO }); setEditando(false); };

  const abrirEdicion = (u) => {
    const v = u.Valid || {};
    setForm({
      employee_no: u.employeeNo,
      nombre: u.name || '',
      password: '',
      valid_desde: v.beginTime || VACIO.valid_desde,
      valid_hasta: v.endTime || VACIO.valid_hasta,
      activo: v.enable !== false,
    });
    setEditando(true);
  };

  const guardar = async () => {
    if (!form.employee_no || !form.nombre) {
      setMsg({ t: 'err', x: 'Número de empleado y nombre son obligatorios' });
      return;
    }
    setGuardando(true);
    setMsg(null);
    try {
      const url = '/api/access/' + deviceId + '/users'
                + (editando ? '/' + form.employee_no : '');
      const r = editando ? await api.put(url, form) : await api.post(url, form);
      if (r.data && r.data.ok) {
        setMsg({ t: 'ok', x: (editando ? 'Cambios guardados' : 'Persona dada de alta')
                             + ' (vía ' + r.data.via + ')' });
        setForm(null);
        await loadUsers();
      } else {
        setMsg({ t: 'err', x: (r.data && r.data.error) || 'El terminal rechazó la operación' });
      }
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setGuardando(false); }
  };

  // ---- desactivar / eliminar -----------------------------------------
  const cambiarActivo = async (u, activo) => {
    setMsg(null);
    try {
      const r = await api.post('/api/access/' + deviceId + '/users/' + u.employeeNo
                               + '/activo?activo=' + activo);
      if (r.data && r.data.ok) {
        setMsg({ t: 'ok', x: activo
          ? 'Persona reactivada'
          : 'Persona desactivada (conserva su biometría)' });
        await loadUsers();
      } else {
        setMsg({ t: 'err', x: (r.data && r.data.error) || 'No se pudo cambiar el estado' });
      }
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    }
    setConfirmar(null);
  };

  const eliminar = async (u) => {
    setMsg(null);
    try {
      const r = await api.delete('/api/access/' + deviceId + '/users/' + u.employeeNo);
      if (r.data && r.data.ok) {
        setMsg({ t: 'ok', x: 'Persona eliminada del terminal junto con su biometría' });
        await loadUsers();
      } else {
        setMsg({ t: 'err', x: (r.data && r.data.error) || 'No se pudo eliminar' });
      }
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    }
    setConfirmar(null);
  };

  // ---- captura de credenciales ---------------------------------------
  // Capturar y grabar van encadenados: la plantilla suelta en el navegador no
  // sirve de nada y se perderia si el operador recarga.
  const capturarHuella = async (empNo) => {
    setCaptura('Pon el dedo en el lector del terminal…');
    setMsg(null);
    try {
      const c = await api.post('/api/access/' + deviceId
                               + '/capture/fingerprint?finger_no=1&timeout_s=45');
      if (!c.data || !c.data.ok) {
        setMsg({ t: 'err', x: 'No se capturó huella: ' + ((c.data && c.data.error) || 'sin datos') });
        return;
      }
      setCaptura('Guardando huella en el terminal…');
      const g = await api.post('/api/access/' + deviceId + '/users/' + empNo
                               + '/fingerprint?finger_no=1',
                               { finger_data: c.data.finger_data });
      setMsg(g.data && g.data.ok
        ? { t: 'ok', x: 'Huella registrada (calidad ' + (c.data.calidad || '?') + '/100)' }
        : { t: 'err', x: (g.data && g.data.error) || 'No se pudo guardar la huella' });
      await loadUsers();
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setCaptura(null); }
  };

  // El rostro tiene dos caminos y NO son intercambiables:
  //  - capturarRostro(): lo toma la camara del terminal. Funciona tambien por
  //    tunel, asi que es la unica opcion en sucursales remotas.
  //  - subirFoto(): manda un JPG desde aqui. Es multipart binario y el tunel no
  //    lo transporta, asi que exige LAN.
  const capturarRostro = async (empNo) => {
    setCaptura('Colócate frente a la cámara del terminal…');
    setMsg(null);
    try {
      const r = await api.post('/api/access/' + deviceId + '/capture/face?employee_no='
                               + encodeURIComponent(empNo) + '&timeout_s=45');
      setMsg(r.data && r.data.ok
        ? { t: 'ok', x: 'Rostro capturado y guardado en el terminal' }
        : { t: 'err', x: 'No se capturó rostro: ' + ((r.data && r.data.error) || 'sin datos') });
      await loadUsers();
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setCaptura(null); }
  };

  const subirFoto = async (empNo, archivo) => {
    if (!archivo) return;
    setCaptura('Subiendo foto al terminal…');
    setMsg(null);
    try {
      const fd = new FormData();
      fd.append('file', archivo);
      const r = await api.post('/api/access/' + deviceId + '/users/' + empNo + '/face', fd,
                               { headers: { 'Content-Type': 'multipart/form-data' } });
      setMsg(r.data && r.data.ok
        ? { t: 'ok', x: 'Foto enrolada como rostro' }
        : { t: 'err', x: (r.data && r.data.error) || 'El terminal rechazó la foto' });
      await loadUsers();
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setCaptura(null); }
  };

  const capturarTarjeta = async (empNo) => {
    setCaptura('Acerca la tarjeta al lector del terminal…');
    setMsg(null);
    try {
      const c = await api.post('/api/access/' + deviceId + '/capture/card?timeout_s=30');
      if (!c.data || !c.data.ok) {
        setMsg({ t: 'err', x: 'No se leyó tarjeta: ' + ((c.data && c.data.error) || 'sin datos') });
        return;
      }
      setCaptura('Asignando tarjeta…');
      const g = await api.post('/api/access/' + deviceId + '/users/' + empNo
                               + '/card?card_no=' + encodeURIComponent(c.data.card_no));
      setMsg(g.data && g.data.ok
        ? { t: 'ok', x: 'Tarjeta ' + c.data.card_no + ' asignada' }
        : { t: 'err', x: (g.data && g.data.error) || 'No se pudo asignar la tarjeta' });
      await loadUsers();
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setCaptura(null); }
  };

  const abrirHorario = (h) => {
    if (!h) {
      setFormH({ nombre: '', tolerancia_min: 10,
                 dias: DIAS.map((d) => ({ dia_semana: d.n, ...DIA_VACIO,
                                          habilitado: d.n >= 1 && d.n <= 5 })) });
      return;
    }
    const porDia = {};
    (h.dias || []).forEach((d) => { porDia[d.dia_semana] = d; });
    setFormH({
      id: h.id, nombre: h.nombre, tolerancia_min: h.tolerancia_min,
      dias: DIAS.map((d) => {
        const x = porDia[d.n];
        return x ? {
          dia_semana: d.n, habilitado: !!x.habilitado,
          entrada_desde: hhmm(x.entrada_desde), entrada_hasta: hhmm(x.entrada_hasta),
          entrada_oficial: hhmm(x.entrada_oficial),
          salida_desde: hhmm(x.salida_desde), salida_hasta: hhmm(x.salida_hasta),
        } : { dia_semana: d.n, ...DIA_VACIO };
      }),
    });
  };

  const guardarHorario = async () => {
    if (!formH.nombre) { setMsg({ t: 'err', x: 'Ponle nombre al horario' }); return; }
    setGuardandoH(true);
    setMsg(null);
    try {
      const body = {
        ...formH,
        dias: formH.dias.map((d) => ({
          ...d,
          entrada_desde: d.entrada_desde ? d.entrada_desde + ':00' : null,
          entrada_hasta: d.entrada_hasta ? d.entrada_hasta + ':00' : null,
          entrada_oficial: d.entrada_oficial ? d.entrada_oficial + ':00' : null,
          salida_desde: d.salida_desde ? d.salida_desde + ':00' : null,
          salida_hasta: d.salida_hasta ? d.salida_hasta + ':00' : null,
        })),
      };
      const r = await api.post('/api/access/horarios', body);
      setMsg({ t: 'ok', x: 'Horario guardado. Aplícalo al terminal para que empiece a bloquear.' });
      setFormH(null);
      await loadHorarios();
      return r;
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    } finally { setGuardandoH(false); }
    return null;
  };

  const aplicarHorario = async (h) => {
    setMsg(null);
    try {
      const r = await api.post('/api/access/' + deviceId + '/horarios/' + h.id + '/aplicar');
      setMsg(r.data && r.data.ok
        ? { t: 'ok', x: 'Programado en el terminal como plantilla ' + r.data.plan_template_no
                        + ' (' + r.data.franjas + ' franjas). Asígnala a las personas de este horario.' }
        : { t: 'err', x: (r.data && r.data.error) || 'No se pudo aplicar' });
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    }
  };

  const borrarHorario = async (h) => {
    setMsg(null);
    try {
      await api.delete('/api/access/horarios/' + h.id);
      setMsg({ t: 'ok', x: 'Horario eliminado del panel (el plan sigue en el terminal)' });
      await loadHorarios();
    } catch (e) {
      setMsg({ t: 'err', x: (e.response && e.response.data && e.response.data.detail) || e.message });
    }
  };

  const dev = devices.find((d) => d.device_id === deviceId);
  const hoy = new Date().toDateString();
  const marcajesHoy = events.filter(
    (e) => e.es_marcaje && new Date(e.event_time).toDateString() === hoy).length;

  return (
    <div style={S.container}>
      <header style={S.header}>
        <div>
          <h1 style={S.title}><Fingerprint size={26} /> Control de Acceso</h1>
          <p style={S.subtitle}>Checadas y personal de los terminales biométricos</p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          <select style={S.select} value={deviceId} onChange={(e) => setDeviceId(e.target.value)}>
            {devices.map((d) => (
              <option key={d.device_id} value={d.device_id}>{d.name || d.device_id}</option>
            ))}
          </select>
          <button style={S.refreshBtn} onClick={sync} disabled={syncing || !deviceId}>
            <Download size={16} className={syncing ? 'spin' : ''} /> Sincronizar
          </button>
        </div>
      </header>

      {msg && <div style={{ ...S.msgBox, ...(msg.t === 'err' ? S.msgErr : S.msgOk) }}>{msg.x}</div>}

      <div style={S.kpis}>
        <div style={{ ...S.kpi, borderLeftColor: '#3b82f6' }}>
          <div style={S.kpiVal}>{marcajesHoy}</div>
          <div style={S.kpiLab}>Checadas hoy</div>
        </div>
        <div style={{ ...S.kpi, borderLeftColor: '#059669' }}>
          <div style={S.kpiVal}>{summary.marcajes != null ? summary.marcajes : '—'}</div>
          <div style={S.kpiLab}>Checadas (total)</div>
        </div>
        <div style={{ ...S.kpi, borderLeftColor: '#6b7280' }}>
          <div style={S.kpiVal}>{users.length || '—'}</div>
          <div style={S.kpiLab}>Personal enrolado</div>
        </div>
        <div style={{ ...S.kpi, borderLeftColor: dev && dev.via === 'offline' ? '#dc2626' : '#059669' }}>
          <div style={{ ...S.kpiVal, fontSize: 16 }}>{(dev && dev.via) || '—'}</div>
          <div style={S.kpiLab}>Conexión {dev && dev.ip ? '(' + dev.ip + ')' : ''}</div>
        </div>
      </div>

      <div style={S.tabs}>
        <button style={{ ...S.tab, ...(tab === 'checadas' ? S.tabActive : {}) }}
                onClick={() => setTab('checadas')}>
          <ClipboardList size={16} /> Checadas
        </button>
        <button style={{ ...S.tab, ...(tab === 'personal' ? S.tabActive : {}) }}
                onClick={() => setTab('personal')}>
          <Users size={16} /> Personal
        </button>
        <button style={{ ...S.tab, ...(tab === 'horarios' ? S.tabActive : {}) }}
                onClick={() => setTab('horarios')}>
          <CalendarClock size={16} /> Horarios
        </button>
      </div>

      {tab === 'checadas' && (
        <>
          <label style={S.check}>
            <input type="checkbox" checked={soloMarcajes}
                   onChange={(e) => setSoloMarcajes(e.target.checked)} />
            Solo checadas de persona (ocultar eventos de sistema)
          </label>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Fecha y hora</th>
                  <th style={S.th}>Persona</th>
                  <th style={S.th}>Empleado</th>
                  <th style={S.th}>Verificación</th>
                  <th style={S.th}>Estado</th>
                  <th style={S.th}>Puerta</th>
                  <th style={S.th}>Origen</th>
                </tr>
              </thead>
              <tbody>
                {events.length === 0 && (
                  <tr><td style={S.tdEmpty} colSpan={7}>Sin registros</td></tr>
                )}
                {events.map((e) => (
                  <tr key={e.id} style={S.tr}>
                    <td style={S.td}>{fmt(e.event_time)}</td>
                    <td style={{ ...S.td, fontWeight: e.person_name ? 600 : 400 }}>
                      {e.person_name || <span style={{ color: '#9ca3af' }}>sistema</span>}
                    </td>
                    <td style={S.td}>{e.employee_no || '—'}</td>
                    <td style={S.td}>{modoOf(e.verify_mode)}</td>
                    <td style={S.td}><Estado a={e.asistencia} /></td>
                    <td style={S.td}>{e.door_no != null ? e.door_no : '—'}</td>
                    <td style={S.td}>
                      <span style={{ ...S.pill,
                                     background: e.origen === 'push' ? '#dbeafe' : '#f3f4f6',
                                     color: e.origen === 'push' ? '#1d4ed8' : '#6b7280' }}>
                        {e.origen}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'personal' && (
        <>
          <div style={S.barra}>
            <div style={S.hint}>
              Leído directamente del terminal{usersVia ? ' (vía ' + usersVia + ')' : ''}.
              <button style={S.linkBtn} onClick={loadUsers}>
                <RefreshCw size={13} className={usersLoading ? 'spin' : ''} /> Recargar
              </button>
            </div>
            <button style={S.primaryBtn} onClick={abrirAlta} disabled={!deviceId}>
              <Plus size={16} /> Nueva persona
            </button>
          </div>

          {usersErr && (
            <div style={{ ...S.msgBox, ...S.msgErr }}>
              No se pudo leer el personal del terminal: {usersErr}
            </div>
          )}

          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Empleado</th>
                  <th style={S.th}>Nombre</th>
                  <th style={S.th}>Rostro</th>
                  <th style={S.th}>Huella</th>
                  <th style={S.th}>Tarjeta</th>
                  <th style={S.th}>Vigencia</th>
                  <th style={S.th}>Estado</th>
                  <th style={S.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {users.length === 0 && (
                  <tr>
                    <td style={S.tdEmpty} colSpan={8}>
                      {usersLoading ? 'Consultando al terminal…'
                        : usersErr ? 'No se pudo leer el terminal'
                        : 'Sin personal enrolado'}
                    </td>
                  </tr>
                )}
                {users.map((u) => {
                  const activo = !u.Valid || u.Valid.enable !== false;
                  return (
                    <tr key={u.employeeNo} style={S.tr}>
                      <td style={S.td}>{u.employeeNo}</td>
                      <td style={{ ...S.td, fontWeight: 600 }}>{u.name}</td>
                      <td style={S.td}><Cnt n={u.numOfFace} icon={ScanFace} /></td>
                      <td style={S.td}><Cnt n={u.numOfFP} icon={Fingerprint} /></td>
                      <td style={S.td}><Cnt n={u.numOfCard} icon={CreditCard} /></td>
                      <td style={S.td}>
                        {u.Valid && u.Valid.beginTime
                          ? u.Valid.beginTime.slice(0, 10) + ' → ' + (u.Valid.endTime || '').slice(0, 10)
                          : '—'}
                      </td>
                      <td style={S.td}>
                        {activo ? <span style={S.ok}><CheckCircle size={14} /> Activo</span>
                                : <span style={S.no}><XCircle size={14} /> Inactivo</span>}
                      </td>
                      <td style={S.td}>
                        <div style={S.acciones}>
                          <button style={S.iconBtn} title="Editar"
                                  onClick={() => abrirEdicion(u)}>
                            <Pencil size={15} />
                          </button>
                          {activo ? (
                            <button style={{ ...S.iconBtn, color: '#ca8a04' }} title="Desactivar"
                                    onClick={() => setConfirmar({ tipo: 'desactivar', user: u })}>
                              <Ban size={15} />
                            </button>
                          ) : (
                            <button style={{ ...S.iconBtn, color: '#059669' }} title="Reactivar"
                                    onClick={() => cambiarActivo(u, true)}>
                              <Play size={15} />
                            </button>
                          )}
                          <button style={{ ...S.iconBtn, color: '#dc2626' }}
                                  title="Eliminar definitivo"
                                  onClick={() => setConfirmar({ tipo: 'eliminar', user: u })}>
                            <Trash2 size={15} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {tab === 'horarios' && (
        <>
          <div style={S.barra}>
            <div style={S.hint}>
              El horario se guarda aquí y se <b>programa en el terminal</b>: fuera de la
              ventana el equipo rechaza la marca. La hora oficial y la tolerancia se usan
              para clasificar retardos en las checadas.
            </div>
            <button style={S.primaryBtn} onClick={() => abrirHorario(null)}>
              <Plus size={16} /> Nuevo horario
            </button>
          </div>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Horario</th>
                  <th style={S.th}>Días</th>
                  <th style={S.th}>Entrada</th>
                  <th style={S.th}>Tolerancia</th>
                  <th style={S.th}>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {horarios.length === 0 && (
                  <tr><td style={S.tdEmpty} colSpan={5}>Sin horarios definidos</td></tr>
                )}
                {horarios.map((h) => {
                  const act = (h.dias || []).filter((d) => d.habilitado);
                  const uno = act[0];
                  return (
                    <tr key={h.id} style={S.tr}>
                      <td style={{ ...S.td, fontWeight: 600 }}>{h.nombre}</td>
                      <td style={S.td}>{act.length} día(s)</td>
                      <td style={S.td}>
                        {uno ? hhmm(uno.entrada_desde) + ' – ' + hhmm(uno.entrada_hasta)
                             + '  (oficial ' + hhmm(uno.entrada_oficial) + ')' : '—'}
                      </td>
                      <td style={S.td}>{h.tolerancia_min} min</td>
                      <td style={S.td}>
                        <div style={S.acciones}>
                          <button style={S.iconBtn} title="Editar"
                                  onClick={() => abrirHorario(h)}><Pencil size={15} /></button>
                          <button style={{ ...S.iconBtn, color: '#3b82f6' }}
                                  title="Programar en el terminal"
                                  onClick={() => aplicarHorario(h)}><Send size={15} /></button>
                          <button style={{ ...S.iconBtn, color: '#dc2626' }} title="Eliminar"
                                  onClick={() => borrarHorario(h)}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </>
      )}

      {/* ---------------- Modal de horario ---------------- */}
      {formH && (
        <div style={S.overlay} onClick={() => !guardandoH && setFormH(null)}>
          <div style={{ ...S.modal, maxWidth: 780 }} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={S.modalTitle}>{formH.id ? 'Editar horario' : 'Nuevo horario'}</h3>
              <button style={S.iconBtn} onClick={() => setFormH(null)}><X size={18} /></button>
            </div>
            <div style={S.fila}>
              <div style={{ ...S.campo, flex: 2 }}>
                <label style={S.label}>Nombre</label>
                <input style={S.input} value={formH.nombre}
                       placeholder="Ej. Oficina L-V 9am"
                       onChange={(e) => setFormH({ ...formH, nombre: e.target.value })} />
              </div>
              <div style={{ ...S.campo, flex: 1 }}>
                <label style={S.label}>Tolerancia (min)</label>
                <input style={S.input} type="number" min="0" max="120"
                       value={formH.tolerancia_min}
                       onChange={(e) => setFormH({ ...formH,
                         tolerancia_min: parseInt(e.target.value, 10) || 0 })} />
              </div>
            </div>

            <div style={S.tableWrap}>
              <table style={S.table}>
                <thead>
                  <tr>
                    <th style={S.th}></th>
                    <th style={S.th}>Día</th>
                    <th style={S.th}>Entrada desde</th>
                    <th style={S.th}>hasta</th>
                    <th style={S.th}>Hora oficial</th>
                    <th style={S.th}>Salida desde</th>
                    <th style={S.th}>hasta</th>
                  </tr>
                </thead>
                <tbody>
                  {formH.dias.map((d, i) => (
                    <tr key={d.dia_semana} style={S.tr}>
                      <td style={S.td}>
                        <input type="checkbox" checked={d.habilitado}
                               onChange={(e) => {
                                 const dias = [...formH.dias];
                                 dias[i] = { ...d, habilitado: e.target.checked };
                                 setFormH({ ...formH, dias });
                               }} />
                      </td>
                      <td style={{ ...S.td, opacity: d.habilitado ? 1 : 0.45 }}>
                        {(DIAS.find((x) => x.n === d.dia_semana) || {}).lbl}
                      </td>
                      {['entrada_desde', 'entrada_hasta', 'entrada_oficial',
                        'salida_desde', 'salida_hasta'].map((campo) => (
                        <td style={S.td} key={campo}>
                          <input type="time" style={S.inputHora} value={d[campo] || ''}
                                 disabled={!d.habilitado}
                                 onChange={(e) => {
                                   const dias = [...formH.dias];
                                   dias[i] = { ...d, [campo]: e.target.value };
                                   setFormH({ ...formH, dias });
                                 }} />
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <p style={S.credNota}>
              Los días sin marcar quedan <b>sin acceso</b> en el terminal. La ventana de
              entrada es lo que bloquea; la hora oficial más la tolerancia es lo que decide
              si la checada cuenta como retardo.
            </p>

            <div style={S.modalFoot}>
              <button style={S.ghostBtn} onClick={() => setFormH(null)} disabled={guardandoH}>
                Cancelar
              </button>
              <button style={S.primaryBtn} onClick={guardarHorario} disabled={guardandoH}>
                {guardandoH ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Modal alta / edicion ---------------- */}
      {form && (
        <div style={S.overlay} onClick={() => !guardando && setForm(null)}>
          <div style={S.modal} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={S.modalTitle}>{editando ? 'Editar persona' : 'Nueva persona'}</h3>
              <button style={S.iconBtn} onClick={() => setForm(null)}><X size={18} /></button>
            </div>

            <div style={S.campo}>
              <label style={S.label}>Número de empleado</label>
              <input style={{ ...S.input, background: editando ? '#f3f4f6' : 'white' }}
                     value={form.employee_no} disabled={editando}
                     onChange={(e) => setForm({ ...form, employee_no: e.target.value })} />
            </div>
            <div style={S.campo}>
              <label style={S.label}>Nombre completo</label>
              <input style={S.input} value={form.nombre}
                     onChange={(e) => setForm({ ...form, nombre: e.target.value })} />
            </div>
            <div style={S.campo}>
              <label style={S.label}>
                <KeyRound size={13} /> Código numérico {editando ? '(vacío = no cambiar)' : '(opcional)'}
              </label>
              <input style={S.input} value={form.password} inputMode="numeric"
                     onChange={(e) => setForm({ ...form, password: e.target.value })} />
            </div>
            <div style={S.fila}>
              <div style={{ ...S.campo, flex: 1 }}>
                <label style={S.label}>Vigencia desde</label>
                <input style={S.input} type="date" value={(form.valid_desde || '').slice(0, 10)}
                       onChange={(e) => setForm({ ...form, valid_desde: e.target.value + 'T00:00:00' })} />
              </div>
              <div style={{ ...S.campo, flex: 1 }}>
                <label style={S.label}>Vigencia hasta</label>
                <input style={S.input} type="date" value={(form.valid_hasta || '').slice(0, 10)}
                       onChange={(e) => setForm({ ...form, valid_hasta: e.target.value + 'T23:59:59' })} />
              </div>
            </div>
            <label style={S.check}>
              <input type="checkbox" checked={form.activo}
                     onChange={(e) => setForm({ ...form, activo: e.target.checked })} />
              Activo (puede abrir la puerta)
            </label>

            <div style={S.credBox}>
              <div style={S.credTitle}>Credenciales</div>
              {editando ? (
                <>
                  <p style={S.credHint}>
                    La persona debe estar frente a este terminal para capturar.
                  </p>
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                    <button style={S.secondaryBtn} disabled={!!captura}
                            onClick={() => capturarRostro(form.employee_no)}>
                      <Camera size={15} /> Tomar rostro
                    </button>
                    <button style={S.secondaryBtn} disabled={!!captura}
                            onClick={() => fileRef.current && fileRef.current.click()}>
                      <Upload size={15} /> Subir foto
                    </button>
                    <button style={S.secondaryBtn} disabled={!!captura}
                            onClick={() => capturarHuella(form.employee_no)}>
                      <Fingerprint size={15} /> Capturar huella
                    </button>
                    <button style={S.secondaryBtn} disabled={!!captura}
                            onClick={() => capturarTarjeta(form.employee_no)}>
                      <CreditCard size={15} /> Capturar tarjeta
                    </button>
                  </div>
                  <input ref={fileRef} type="file" accept="image/jpeg,image/png"
                         style={{ display: 'none' }}
                         onChange={(e) => {
                           subirFoto(form.employee_no, e.target.files && e.target.files[0]);
                           e.target.value = '';
                         }} />
                  <p style={S.credNota}>
                    <b>Tomar rostro</b> usa la cámara del terminal y funciona aunque el
                    equipo esté en otra sucursal. <b>Subir foto</b> solo funciona si el
                    servidor alcanza al terminal por red local.
                  </p>
                  {captura && <div style={S.capturando}>{captura}</div>}
                </>
              ) : (
                <p style={S.credHint}>
                  Guarda primero a la persona; después podrás capturar su huella
                  o tarjeta desde el botón Editar.
                </p>
              )}
            </div>

            <div style={S.modalFoot}>
              <button style={S.ghostBtn} onClick={() => setForm(null)} disabled={guardando}>
                Cancelar
              </button>
              <button style={S.primaryBtn} onClick={guardar} disabled={guardando}>
                {guardando ? 'Guardando…' : 'Guardar'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ---------------- Confirmacion ---------------- */}
      {confirmar && (
        <div style={S.overlay} onClick={() => setConfirmar(null)}>
          <div style={{ ...S.modal, maxWidth: 460 }} onClick={(e) => e.stopPropagation()}>
            <div style={S.modalHead}>
              <h3 style={S.modalTitle}>
                {confirmar.tipo === 'eliminar' ? 'Eliminar definitivamente' : 'Desactivar persona'}
              </h3>
              <button style={S.iconBtn} onClick={() => setConfirmar(null)}><X size={18} /></button>
            </div>
            <p style={S.confirmTexto}>
              {confirmar.tipo === 'eliminar' ? (
                <>
                  Se borrará a <b>{confirmar.user.name}</b> ({confirmar.user.employeeNo}) del
                  terminal, <b>junto con su rostro, huellas y tarjetas</b>. Si vuelve, habrá
                  que enrolarla desde cero.
                </>
              ) : (
                <>
                  <b>{confirmar.user.name}</b> ({confirmar.user.employeeNo}) dejará de poder
                  abrir la puerta, pero <b>conserva su biometría</b>. Puedes reactivarla
                  cuando quieras sin volver a capturar nada.
                </>
              )}
            </p>
            <div style={S.modalFoot}>
              <button style={S.ghostBtn} onClick={() => setConfirmar(null)}>Cancelar</button>
              <button style={confirmar.tipo === 'eliminar' ? S.dangerBtn : S.warnBtn}
                      onClick={() => (confirmar.tipo === 'eliminar'
                        ? eliminar(confirmar.user)
                        : cambiarActivo(confirmar.user, false))}>
                {confirmar.tipo === 'eliminar' ? 'Sí, eliminar' : 'Sí, desactivar'}
              </button>
            </div>
          </div>
        </div>
      )}

      <style>{'.spin{animation:spin 1s linear infinite}@keyframes spin{to{transform:rotate(360deg)}}'}</style>
    </div>
  );
};

const Estado = ({ a }) => {
  if (!a || !a.estado) return <span style={{ color: '#d1d5db' }}>—</span>;
  const e = ESTADO[a.estado] || ESTADO.sin_horario;
  const txt = a.estado === 'retardo' && a.minutos ? e.txt + ' ' + a.minutos + "'" : e.txt;
  return <span style={{ ...S.pill, background: e.bg, color: e.c }}>{txt}</span>;
};

const Cnt = ({ n, icon: Icon }) => (
  n > 0
    ? <span style={S.ok}><Icon size={14} /> {n}</span>
    : <span style={{ color: '#d1d5db' }}>—</span>
);

const S = {
  container: { padding: 24, maxWidth: 1300, margin: '0 auto' },
  header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 20, flexWrap: 'wrap', gap: 12 },
  title: { fontSize: 24, fontWeight: 700, margin: 0, display: 'flex', alignItems: 'center', gap: 10 },
  subtitle: { fontSize: 13, color: '#6b7280', marginTop: 4 },
  select: { padding: '8px 12px', border: '1px solid #e5e7eb', borderRadius: 8,
            background: 'white', fontSize: 14 },
  refreshBtn: { display: 'flex', gap: 6, alignItems: 'center', padding: '8px 14px',
                background: 'white', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' },
  primaryBtn: { display: 'inline-flex', gap: 6, alignItems: 'center', padding: '8px 16px',
                background: '#3b82f6', color: 'white', border: 'none', borderRadius: 8,
                cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  secondaryBtn: { display: 'inline-flex', gap: 6, alignItems: 'center', padding: '8px 14px',
                  background: 'white', color: '#374151', border: '1px solid #d1d5db',
                  borderRadius: 8, cursor: 'pointer', fontSize: 13 },
  ghostBtn: { padding: '8px 16px', background: 'white', border: '1px solid #e5e7eb',
              borderRadius: 8, cursor: 'pointer', fontSize: 14 },
  dangerBtn: { padding: '8px 16px', background: '#dc2626', color: 'white', border: 'none',
               borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  warnBtn: { padding: '8px 16px', background: '#ca8a04', color: 'white', border: 'none',
             borderRadius: 8, cursor: 'pointer', fontSize: 14, fontWeight: 600 },
  iconBtn: { background: 'none', border: 'none', cursor: 'pointer', padding: 4,
             color: '#6b7280', display: 'inline-flex', alignItems: 'center' },

  msgBox: { padding: '10px 14px', borderRadius: 8, marginBottom: 14, fontSize: 13 },
  msgOk: { background: '#d1fae5', color: '#065f46' },
  msgErr: { background: '#fee2e2', color: '#991b1b' },

  kpis: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
          gap: 12, marginBottom: 20 },
  kpi: { background: 'white', padding: 14, borderRadius: 8, borderLeft: '4px solid',
         boxShadow: '0 1px 2px rgba(0,0,0,0.05)' },
  kpiVal: { fontSize: 22, fontWeight: 700 },
  kpiLab: { fontSize: 12, color: '#6b7280', marginTop: 4 },

  tabs: { display: 'flex', gap: 8, marginBottom: 16 },
  tab: { padding: '8px 16px', borderRadius: 8, border: '1px solid #e5e7eb', background: 'white',
         cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', gap: 6 },
  tabActive: { background: '#3b82f6', color: 'white', borderColor: '#3b82f6' },

  barra: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
           marginBottom: 12, gap: 12, flexWrap: 'wrap' },
  check: { display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
           color: '#374151', marginBottom: 12 },
  hint: { fontSize: 12, color: '#6b7280', display: 'flex', alignItems: 'center', gap: 10 },
  linkBtn: { display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none',
             border: 'none', color: '#3b82f6', cursor: 'pointer', fontSize: 12, padding: 0 },

  tableWrap: { background: 'white', borderRadius: 8, overflow: 'hidden',
               boxShadow: '0 1px 2px rgba(0,0,0,0.05)', overflowX: 'auto' },
  table: { width: '100%', borderCollapse: 'collapse', fontSize: 13 },
  th: { textAlign: 'left', padding: '10px 14px', background: '#f9fafb', color: '#6b7280',
        fontWeight: 600, borderBottom: '1px solid #e5e7eb', whiteSpace: 'nowrap' },
  tr: { borderBottom: '1px solid #f3f4f6' },
  td: { padding: '10px 14px', color: '#374151', whiteSpace: 'nowrap' },
  tdEmpty: { padding: 40, textAlign: 'center', color: '#9ca3af' },
  pill: { padding: '2px 8px', borderRadius: 4, fontSize: 11, fontWeight: 600 },
  ok: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#059669' },
  no: { display: 'inline-flex', alignItems: 'center', gap: 4, color: '#9ca3af' },
  acciones: { display: 'flex', gap: 2 },

  overlay: { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', display: 'flex',
             alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: 16 },
  modal: { background: 'white', borderRadius: 12, padding: 20, width: '100%', maxWidth: 520,
           maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 20px 40px rgba(0,0,0,0.2)' },
  modalHead: { display: 'flex', justifyContent: 'space-between', alignItems: 'center',
               marginBottom: 16 },
  modalTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  modalFoot: { display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 20 },
  campo: { marginBottom: 14 },
  fila: { display: 'flex', gap: 12 },
  label: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 12, fontWeight: 600,
           color: '#6b7280', marginBottom: 5 },
  input: { width: '100%', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: 8,
           fontSize: 14, boxSizing: 'border-box' },
  credBox: { background: '#f9fafb', borderRadius: 8, padding: 14, marginTop: 4 },
  credTitle: { fontSize: 13, fontWeight: 700, color: '#374151', marginBottom: 4 },
  credHint: { fontSize: 12, color: '#6b7280', margin: '0 0 10px' },
  capturando: { marginTop: 10, fontSize: 13, color: '#1d4ed8', fontWeight: 600 },
  credNota: { fontSize: 11, color: '#9ca3af', margin: '10px 0 0', lineHeight: 1.5 },
  inputHora: { padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: 6,
               fontSize: 12, width: 108, boxSizing: 'border-box' },
  confirmTexto: { fontSize: 14, color: '#374151', lineHeight: 1.5, margin: 0 },
};

export default Accesos;
